// Component style overrides - Modern, elegant design
export const components = {
  Button: {
    baseStyle: {
      fontWeight: '500',
      borderRadius: 'md',
      transition: 'background 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
      _hover: {
        transform: 'none',
      },
      _active: {
        transform: 'none',
      },
    },
    sizes: {
      sm: {
        fontSize: 'sm',
        px: 3,
        py: 1.5,
        h: '32px',
      },
      md: {
        fontSize: '14px',
        px: 5,
        py: 2,
        h: '36px',
      },
      lg: {
        fontSize: 'md',
        px: 6,
        py: 2.5,
        h: '40px',
      },
    },
    variants: {
      solid: {
        bg: '#0052CC',
        color: 'white',
        boxShadow: 'none',
        css: {
          backgroundColor: '#0052CC',
          color: 'white',
          '&:hover:not(:disabled)': {
            backgroundColor: '#0747A6',
          },
          '&:active:not(:disabled)': {
            backgroundColor: '#0747A6',
          },
        },
        _hover: {
          bg: '#0747A6',
          _disabled: {
            bg: '#0052CC',
          },
        },
        _active: {
          bg: '#0747A6',
        },
      },
      secondary: {
        bg: '#F4F5F7',
        color: '#42526E',
        boxShadow: 'none',
        _hover: {
          bg: '#EBECF0',
          _disabled: {
            bg: '#F4F5F7',
          },
        },
        _active: {
          bg: '#DFE1E6',
        },
      },
      outline: {
        border: '1px solid',
        borderColor: '#DFE1E6',
        color: '#42526E',
        bg: 'white',
        _hover: {
          bg: '#F4F5F7',
          borderColor: '#B3BAC5',
        },
        _active: {
          bg: '#EBECF0',
        },
      },
      ghost: {
        color: '#5E6C84',
        bg: 'transparent',
        _hover: {
          bg: '#F4F5F7',
        },
        _active: {
          bg: '#EBECF0',
        },
      },
    },
    defaultProps: {
      size: 'md',
      variant: 'solid',
    },
  },

  IconButton: {
    baseStyle: {
      borderRadius: 'sm',
      transition: 'background 0.1s ease-in-out',
      _hover: {
        transform: 'none',
      },
      _active: {
        transform: 'none',
      },
    },
    sizes: {
      sm: {
        h: '32px',
        minW: '32px',
        fontSize: 'md',
      },
      md: {
        h: '36px',
        minW: '36px',
        fontSize: 'lg',
      },
      lg: {
        h: '40px',
        minW: '40px',
        fontSize: 'xl',
      },
    },
    variants: {
      solid: {
        bg: '#0052CC',
        color: 'white',
        boxShadow: 'none',
        _hover: {
          bg: '#0747A6',
          _disabled: {
            bg: '#0052CC',
          },
        },
        _active: {
          bg: '#0747A6',
        },
        _disabled: {
          opacity: 0.4,
          cursor: 'not-allowed',
        },
      },
      ghost: {
        color: '#5E6C84',
        bg: 'transparent',
        _hover: {
          bg: '#F4F5F7',
          _disabled: {
            bg: 'transparent',
          },
        },
        _active: {
          bg: '#EBECF0',
        },
        _disabled: {
          opacity: 0.4,
          cursor: 'not-allowed',
        },
      },
      outline: {
        border: '1px solid',
        borderColor: '#DFE1E6',
        color: '#42526E',
        bg: 'white',
        _hover: {
          bg: '#F4F5F7',
          _disabled: {
            bg: 'white',
          },
        },
        _active: {
          bg: '#EBECF0',
        },
        _disabled: {
          opacity: 0.4,
          cursor: 'not-allowed',
        },
      },
    },
    defaultProps: {
      size: 'md',
      variant: 'ghost',
    },
  },

  Input: {
    baseStyle: {
      field: {
        borderRadius: 'sm',
        transition: 'border-color 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
      },
    },
    variants: {
      outline: {
        field: {
          border: '1px solid',
          borderColor: '#DFE1E6',
          bg: 'white',
          _hover: {
            borderColor: '#B3BAC5',
          },
          _focus: {
            borderColor: '#4C9AFF',
            boxShadow: '0 0 0 1px #4C9AFF',
          },
          _placeholder: {
            color: '#8993A4',
          },
        },
      },
    },
    defaultProps: {
      variant: 'outline',
    },
  },

  Card: {
    baseStyle: {
      container: {
        bg: 'white',
        borderRadius: 'sm',
        border: '1px solid',
        borderColor: '#DFE1E6',
        boxShadow: 'none',
        overflow: 'hidden',
      },
      header: {
        py: 3,
        px: 4,
        borderBottom: '1px solid',
        borderColor: '#EBECF0',
        bg: '#FAFBFC',
      },
      body: {
        py: 4,
        px: 4,
      },
      footer: {
        py: 3,
        px: 4,
        borderTop: '1px solid',
        borderColor: '#EBECF0',
      },
    },
  },

  Modal: {
    baseStyle: {
      dialog: {
        bg: 'white',
        borderRadius: 'md',
        boxShadow: 'xl',
      },
      overlay: {
        bg: 'rgba(9, 30, 66, 0.54)',
        backdropFilter: 'blur(1px)',
      },
      header: {
        fontSize: 'xl',
        fontWeight: '600',
        pb: 4,
      },
      closeButton: {
        borderRadius: 'md',
        _hover: {
          bg: 'gray.100',
        },
      },
    },
  },

  Table: {
    variants: {
      simple: {
        th: {
          borderColor: '#EBECF0',
          borderBottomWidth: '1px',
          borderRightWidth: '1px',
          color: '#5E6C84',
          fontSize: '11px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: 'wide',
          py: 2.5,
          px: 3,
          bg: '#F4F5F7',
          '&:last-child': {
            borderRightWidth: '0',
          },
        },
        td: {
          borderColor: '#EBECF0',
          borderBottomWidth: '1px',
          borderRightWidth: '1px',
          py: 2.5,
          px: 3,
          fontSize: 'sm',
          '&:last-child': {
            borderRightWidth: '0',
          },
        },
        tbody: {
          tr: {
            transition: 'background 0.1s ease-in-out',
            _hover: {
              bg: '#DEEBFF',
            },
          },
        },
      },
    },
  },

  Badge: {
    baseStyle: {
      px: 2,
      py: 0.5,
      borderRadius: 'sm',
      fontSize: 'xs',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 'wide',
    },
  },

  Tooltip: {
    baseStyle: {
      borderRadius: 'sm',
      bg: 'gray.800',
      color: 'white',
      px: 2,
      py: 1,
      fontSize: 'xs',
      boxShadow: 'md',
    },
  },

  Menu: {
    baseStyle: {
      list: {
        borderRadius: 'sm',
        border: '1px solid',
        borderColor: '#DFE1E6',
        boxShadow: '0 4px 8px 0 rgba(9, 30, 66, 0.12), 0 0 1px 0 rgba(9, 30, 66, 0.12)',
        py: 1,
        minW: '200px',
      },
      item: {
        borderRadius: 'none',
        mx: 0,
        py: 1.5,
        px: 2.5,
        fontSize: 'sm',
        transition: 'background 0.1s ease-in-out',
        _hover: {
          bg: '#F4F5F7',
        },
        _focus: {
          bg: '#F4F5F7',
        },
      },
    },
  },
}
